import dayjs from 'dayjs';
import {
  ITEM_KIND, allMoves, avgCost, daysToExpiry, itemById, lotsAt, stockLots, type Item, type ItemKind, type KhoState, type Move, type Supplier, type Warehouse,
} from './khoLogic';

/**
 * Chỉ số quản trị kho (cho trang Tổng quan): dòng tiền nhập/xuất theo kỳ, so sánh kỳ trước, số ngày tồn kho,
 * dự báo hết hàng theo tốc độ dùng thực tế, hàng tồn đọng, rủi ro hết hạn, phân loại ABC, nhà cung cấp, kiểm kê.
 * "Xuất dùng" = xuất dịch vụ + xuất bán (không tính chuyển kho nội bộ). "Hao hụt" = xuất hủy + thiếu khi kiểm kê.
 */

const D = 86400000;
const val = (m: Move) => Math.abs(m.qty) * m.unitCost;
const inScope = (m: Move, wh?: string) => !wh || m.warehouseId === wh;
const isUse = (m: Move) => m.type === 'issue_service' || m.type === 'issue_sale';

export interface PeriodFlows {
  /** Nhập mua */
  imported: number;
  /** Chuyển đến / chuyển đi (chỉ có ý nghĩa khi xem 1 kho) */
  transferIn: number;
  transferOut: number;
  service: number;
  sale: number;
  /** Doanh thu bán lẻ mỹ phẩm (giá bán × SL) */
  saleRevenue: number;
  disposed: number;
  countLoss: number;
  countGain: number;
  /** Xuất dùng = dịch vụ + bán */
  used: number;
  /** Hao hụt = hủy + thiếu kiểm kê */
  shrink: number;
  sessions: number;
}

export function periodFlows(s: KhoState, from: number, to: number, wh?: string): PeriodFlows {
  const f: PeriodFlows = { imported: 0, transferIn: 0, transferOut: 0, service: 0, sale: 0, saleRevenue: 0, disposed: 0, countLoss: 0, countGain: 0, used: 0, shrink: 0, sessions: 0 };
  for (const m of allMoves(s)) {
    if (m.at < from || m.at > to || !inScope(m, wh)) continue;
    const v = val(m);
    if (m.type === 'import') f.imported += v;
    else if (m.type === 'transfer') { if (m.qty > 0) f.transferIn += v; else f.transferOut += v; }
    else if (m.type === 'issue_service') f.service += v;
    else if (m.type === 'issue_sale') { f.sale += v; f.saleRevenue += Math.abs(m.qty) * (itemById(s, m.itemId)?.price || 0); }
    else if (m.type === 'dispose') f.disposed += v;
    else if (m.type === 'count') { if (m.qty < 0) f.countLoss += v; else f.countGain += v; }
  }
  f.used = f.service + f.sale;
  f.shrink = f.disposed + f.countLoss;
  f.sessions = s.docs.filter(d => d.type === 'issue_service' && d.status === 'approved' && d.date >= from && d.date <= to && (!wh || d.warehouseId === wh)).length;
  return f;
}

/** Giá trị tồn kho tại thời điểm t */
export const stockValueAt = (s: KhoState, t: number, wh?: string) =>
  lotsAt(s, t).filter(l => l.qty > 0 && (!wh || l.warehouseId === wh)).reduce((a, l) => a + l.qty * (l.unitCost || avgCost(s, l.itemId)), 0);

/** % thay đổi so với kỳ trước (null khi kỳ trước = 0) */
export const pctChange = (cur: number, prev: number) => (prev ? Math.round(((cur - prev) / prev) * 100) : null);

export interface SeriesPoint { label: string; from: number; imported: number; used: number; shrink: number; /** Giá trị tồn cuối mốc */ stock: number }

/** Nhập – xuất dùng – hao hụt theo ngày (kỳ ≤ 31 ngày) hoặc theo tuần */
export function flowSeries(s: KhoState, from: number, to: number, wh?: string): SeriesPoint[] {
  const days = Math.ceil((to - from) / D);
  const step = days <= 31 ? 1 : 7;
  const pts: SeriesPoint[] = [];
  for (let t = dayjs(from).startOf('day'); t.valueOf() <= to; t = t.add(step, 'day')) {
    pts.push({ label: step === 1 ? t.format('DD/MM') : `Tuần ${t.format('DD/MM')}`, from: t.valueOf(), imported: 0, used: 0, shrink: 0, stock: 0 });
  }
  for (const m of allMoves(s)) {
    if (m.at < from || m.at > to || !inScope(m, wh)) continue;
    const idx = Math.min(pts.length - 1, Math.floor((dayjs(m.at).startOf('day').valueOf() - pts[0].from) / (step * D)));
    if (idx < 0) continue;
    const p = pts[idx];
    if (m.type === 'import') p.imported += val(m);
    else if (isUse(m)) p.used += val(m);
    else if (m.type === 'dispose' || (m.type === 'count' && m.qty < 0)) p.shrink += val(m);
  }
  for (const p of pts) p.stock = stockValueAt(s, Math.min(to, p.from + step * D - 1), wh);
  return pts;
}

export interface CoverRow { item: Item; qty: number; dailyUse: number; days: number | null; low: boolean }

/** Dự báo hết hàng: tồn hiện tại ÷ lượng dùng bình quân/ngày trong `lookback` ngày */
export function stockCover(s: KhoState, now: number, wh?: string, lookback = 30): CoverRow[] {
  const from = now - lookback * D;
  const used = new Map<string, number>();
  for (const m of allMoves(s)) {
    if (m.at < from || m.at > now || !isUse(m) || !inScope(m, wh)) continue;
    used.set(m.itemId, (used.get(m.itemId) || 0) - m.qty);
  }
  const lots = stockLots(s);
  return s.items.filter(i => i.active).map(item => {
    const qty = lots.filter(l => l.itemId === item.id && (!wh || l.warehouseId === wh)).reduce((a, l) => a + l.qty, 0);
    const total = lots.filter(l => l.itemId === item.id).reduce((a, l) => a + l.qty, 0);
    const dailyUse = (used.get(item.id) || 0) / lookback;
    return { item, qty, dailyUse, days: dailyUse > 0 ? qty / dailyUse : null, low: item.minStock > 0 && total < item.minStock };
  });
}

export interface SlowRow { item: Item; qty: number; value: number; lastUse: number | null; idleDays: number }

/** Hàng tồn đọng: còn hàng nhưng không xuất dùng trong `days` ngày (vốn bị chôn) */
export function slowMoving(s: KhoState, now: number, wh?: string, days = 45): SlowRow[] {
  const last = new Map<string, number>();
  const firstIn = new Map<string, number>();
  for (const m of allMoves(s)) {
    if (!inScope(m, wh)) continue;
    if (isUse(m)) last.set(m.itemId, Math.max(last.get(m.itemId) || 0, m.at));
    if (m.qty > 0 && !firstIn.has(m.itemId)) firstIn.set(m.itemId, m.at);
  }
  const lots = stockLots(s);
  return s.items.map(item => {
    const mine = lots.filter(l => l.itemId === item.id && (!wh || l.warehouseId === wh));
    const qty = mine.reduce((a, l) => a + l.qty, 0);
    const lastUse = last.get(item.id) ?? null;
    const since = lastUse ?? firstIn.get(item.id) ?? now;
    return { item, qty, value: mine.reduce((a, l) => a + l.qty * l.unitCost, 0), lastUse, idleDays: Math.floor((now - since) / D) };
  }).filter(r => r.qty > 0 && r.idleDays >= days && r.item.kind !== 'tool').sort((a, b) => b.value - a.value);
}

export interface ExpiryBucket { key: string; label: string; value: number; lots: number; tone: string }

/** Giá trị hàng theo mốc hạn dùng */
export function expiryBuckets(s: KhoState, now: number, wh?: string): ExpiryBucket[] {
  const b: ExpiryBucket[] = [
    { key: 'expired', label: 'Đã hết hạn', value: 0, lots: 0, tone: 'bg-rose-500' },
    { key: '30', label: '≤ 30 ngày', value: 0, lots: 0, tone: 'bg-orange-500' },
    { key: '60', label: '31–60 ngày', value: 0, lots: 0, tone: 'bg-amber-400' },
    { key: '90', label: '61–90 ngày', value: 0, lots: 0, tone: 'bg-yellow-300' },
  ];
  for (const l of stockLots(s)) {
    if (wh && l.warehouseId !== wh) continue;
    const d = daysToExpiry(l.expiry, now);
    if (d === null || d > 90) continue;
    const x = d < 0 ? b[0] : d <= 30 ? b[1] : d <= 60 ? b[2] : b[3];
    x.value += l.qty * l.unitCost;
    x.lots += 1;
  }
  return b;
}

export interface AbcRow { item: Item; value: number; share: number; cumulative: number; cls: 'A' | 'B' | 'C' }

/** Phân loại ABC theo giá trị xuất dùng trong kỳ: A ≈ 80% giá trị, B ≈ 15%, C phần còn lại */
export function abcAnalysis(s: KhoState, from: number, to: number, wh?: string): AbcRow[] {
  const by = new Map<string, number>();
  for (const m of allMoves(s)) {
    if (m.at < from || m.at > to || !isUse(m) || !inScope(m, wh)) continue;
    by.set(m.itemId, (by.get(m.itemId) || 0) + val(m));
  }
  const total = Array.from(by.values()).reduce((a, v) => a + v, 0);
  let cum = 0;
  return Array.from(by.entries())
    .map(([id, value]) => ({ item: itemById(s, id)!, value }))
    .filter(r => r.item)
    .sort((a, b) => b.value - a.value)
    .map(r => {
      const share = total ? r.value / total : 0;
      const before = cum;
      cum += share;
      return { ...r, share, cumulative: cum, cls: before < 0.8 ? 'A' : before < 0.95 ? 'B' : 'C' } as AbcRow;
    });
}

export interface PriceChange { item: Item; supplier: Supplier | null; prev: number; last: number; pct: number; at: number }
export interface SupplierRow { supplier: Supplier; value: number; docs: number; share: number }

/** Giá trị nhập theo nhà cung cấp trong kỳ + biến động giá nhập (lần nhập gần nhất so với lần trước) */
export function supplierInsights(s: KhoState, from: number, to: number) {
  const imports = s.docs.filter(d => d.type === 'import' && d.status === 'approved').sort((a, b) => a.date - b.date);
  const rows = new Map<string, SupplierRow>();
  let total = 0;
  for (const d of imports) {
    if (d.date < from || d.date > to || !d.supplierId) continue;
    const sup = s.suppliers.find(x => x.id === d.supplierId);
    if (!sup) continue;
    const v = d.lines.reduce((a, l) => a + l.qty * l.unitCost, 0);
    const r = rows.get(sup.id) || { supplier: sup, value: 0, docs: 0, share: 0 };
    r.value += v;
    r.docs += 1;
    total += v;
    rows.set(sup.id, r);
  }
  const prices = new Map<string, { cost: number; at: number; supplierId?: string }[]>();
  for (const d of imports) for (const l of d.lines) {
    const arr = prices.get(l.itemId) || [];
    arr.push({ cost: l.unitCost, at: d.date, supplierId: d.supplierId });
    prices.set(l.itemId, arr);
  }
  const changes: PriceChange[] = [];
  prices.forEach((arr, itemId) => {
    if (arr.length < 2) return;
    const last = arr[arr.length - 1];
    const prev = [...arr].reverse().find(x => x.at < last.at);
    const item = itemById(s, itemId);
    if (!prev || !item || prev.cost === last.cost) return;
    changes.push({ item, supplier: s.suppliers.find(x => x.id === last.supplierId) || null, prev: prev.cost, last: last.cost, pct: Math.round(((last.cost - prev.cost) / prev.cost) * 1000) / 10, at: last.at });
  });
  return {
    suppliers: Array.from(rows.values()).map(r => ({ ...r, share: total ? r.value / total : 0 })).sort((a, b) => b.value - a.value),
    priceChanges: changes.sort((a, b) => b.pct - a.pct),
  };
}

export interface CountStatus { warehouse: Warehouse; lastCount: number | null; days: number | null; overdue: boolean; accuracy: number | null; diffValue: number }

/** Lịch kiểm kê: lần kiểm gần nhất từng kho, quá hạn kiểm định kỳ, độ chính xác sổ sách lần đó */
export function countStatus(s: KhoState, now: number, everyDays = 30): CountStatus[] {
  return s.warehouses.map(w => {
    const last = s.docs.filter(d => d.type === 'count' && d.status === 'approved' && d.warehouseId === w.id).sort((a, b) => b.date - a.date)[0];
    if (!last) return { warehouse: w, lastCount: null, days: null, overdue: true, accuracy: null, diffValue: 0 };
    const days = Math.floor((now - last.date) / D);
    const exact = last.lines.filter(l => (l.counted ?? 0) === (l.systemQty ?? 0)).length;
    return {
      warehouse: w, lastCount: last.date, days, overdue: days > everyDays,
      accuracy: last.lines.length ? Math.round((exact / last.lines.length) * 100) : null,
      diffValue: last.lines.reduce((a, l) => a + ((l.counted ?? 0) - (l.systemQty ?? 0)) * l.unitCost, 0),
    };
  });
}

/** Cơ cấu giá trị tồn theo loại hàng và theo kho */
export function stockMix(s: KhoState, wh?: string) {
  const byKind = new Map<ItemKind, number>();
  const byWh = new Map<string, number>();
  for (const l of stockLots(s)) {
    const item = itemById(s, l.itemId);
    if (!item) continue;
    const v = l.qty * l.unitCost;
    byWh.set(l.warehouseId, (byWh.get(l.warehouseId) || 0) + v);
    if (wh && l.warehouseId !== wh) continue;
    byKind.set(item.kind, (byKind.get(item.kind) || 0) + v);
  }
  return {
    kinds: (Object.keys(ITEM_KIND) as ItemKind[]).map(k => ({ kind: k, label: ITEM_KIND[k].label, value: byKind.get(k) || 0 })).filter(x => x.value > 0).sort((a, b) => b.value - a.value),
    warehouses: s.warehouses.map(w => ({ warehouse: w, value: byWh.get(w.id) || 0 })),
  };
}
